import React, { useEffect, useRef } from 'react';
import type { PropsWithChildren } from 'react';

import isEqual from 'lodash/isEqual';

import type { ModalOptions } from '../../shared/types';

import { BaseMembershipModalStateContextProvider, useMembershipModalState } from './context';

/**
 * The createStateContext from the core library only initializes state once with useState.
 * When props to the provider change, the context state doesn't update.
 * It wasn't a problem with the old ds modal, because it was initialized with the function call.
 * But with the new ds modal, it's added as component to parent page component, so it's initialized on mount.
 * so any changes to the props after the initial mount won't update the context state.
 * StateUpdater is needed to keep the membershipModalState context in sync
 * with the latest ModalOptions passed to the provider.
 */

function StateUpdater(props: ModalOptions) {
	const [, { setStateData }] = useMembershipModalState();
	const prevPropsRef = useRef<ModalOptions>();

	useEffect(() => {
		const prevProps = prevPropsRef.current;
		if (prevProps && !isEqual(prevProps, props)) {
			setStateData(props);
		}

		prevPropsRef.current = props;
	}, [props, setStateData]);

	return null;
}

export const MembershipModalStateContextProvider: React.FC<PropsWithChildren<ModalOptions>> = ({
	children,
	...props
}) => {
	return (
		<BaseMembershipModalStateContextProvider {...props}>
			<StateUpdater {...props} />
			{children}
		</BaseMembershipModalStateContextProvider>
	);
};
